import { Driver, Maybe, Stop } from "@api/graphql/generated/generated-types";
import {
  BaseModal,
  BaseModalBody,
  BaseModalFooter,
  BaseModalHeader,
  Loading,
} from "@src/common/components";
import CourieButton from "@src/common/components/Button";
import { DriverComplianceWarningContent } from "@src/driverCompliancePolicies/components/DriverComplianceWarningContent";
import DriverSelectorList from "@src/shipments/components/ChangeDriverModal/DriverSelectorList";
import { SIZE } from "baseui/modal";
import { useTransferStops } from "./hooks/useTransferStops";
import { pluralizeOrders } from "./utils/transferStopsUtils";

interface TransferStopsModalProps {
  isOpen: boolean;
  onClose: () => void;
  // The job has been applied: the caller can open the target driver's route
  // for review, the same way bulk Assign Driver does.
  onTransferred: (targetDriver: Driver, stopDates: string[]) => void;
  // The job is still queued after the wait limit. Nothing has been confirmed,
  // so the caller should refresh and close without claiming success.
  onPending: () => void;
  selectedStops: Stop[];
  currentDriver?: Maybe<Driver>;
  shipmentDate: string;
}

/**
 * Moves the stops checked in the route list over to another driver.
 *
 * Dispatch is per order, not per stop, so every stop of a checked order moves
 * with it — the body spells that out before the dispatcher confirms.
 *
 * The transfer goes through a quick-dispatch routing job rather than a
 * per-shipment dispatch loop: one optimization pass covers the whole batch and
 * the target driver's existing sequence is preserved. Once the job is applied
 * the caller gets the target driver back so it can show the resulting route.
 */
export function TransferStopsModal({
  isOpen,
  onClose,
  onTransferred,
  onPending,
  selectedStops,
  currentDriver,
  shipmentDate,
}: TransferStopsModalProps) {
  const {
    selectedDriver,
    setSelectedDriver,
    shipmentIds,
    driverListDate,
    driverGroupDefinitions,
    driverGroupsLoading,
    handleTransfer,
    canTransfer,
    isTransferring,
    complianceWarning,
  } = useTransferStops({
    isOpen,
    selectedStops,
    shipmentDate,
    onTransferred,
    onPending,
  });

  if (!isOpen) {
    return null;
  }

  if (complianceWarning.isOpen) {
    return (
      <BaseModal
        isOpen={isOpen}
        onClose={complianceWarning.close}
        size={SIZE.auto}
      >
        <DriverComplianceWarningContent
          warningTypes={complianceWarning.warningTypes}
          onClose={complianceWarning.close}
          onProceed={complianceWarning.proceed}
        />
      </BaseModal>
    );
  }

  return (
    // The modal's default autofocus lands on the driver search input.
    <BaseModal
      isOpen={isOpen}
      closeable={true}
      onClose={onClose}
      size={SIZE.auto}
    >
      <BaseModalHeader>
        <span className="text-gray-900 dark:text-gray-100">
          Transfer {pluralizeOrders(shipmentIds.length)}
        </span>
      </BaseModalHeader>
      <BaseModalBody style={{ minWidth: 520 }}>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {selectedStops.length}{" "}
          {selectedStops.length === 1 ? "stop is" : "stops are"} selected from{" "}
          {currentDriver?.firstName || "the current driver"}. Every stop of the
          selected orders moves to the driver you pick below.
        </p>
        <Loading loading={driverGroupsLoading}>
          <DriverSelectorList
            selectedDriver={selectedDriver}
            setSelectedDriver={setSelectedDriver}
            hideDriverIds={currentDriver?.id ? [currentDriver.id] : undefined}
            date={driverListDate}
            driverGroupDefinitions={driverGroupDefinitions}
          />
        </Loading>
      </BaseModalBody>
      <BaseModalFooter className="flex justify-between">
        <CourieButton color="secondary" onClick={onClose}>
          Cancel
        </CourieButton>
        <CourieButton
          isProcessing={isTransferring}
          disabled={!canTransfer}
          onClick={handleTransfer}
        >
          Transfer
        </CourieButton>
      </BaseModalFooter>
    </BaseModal>
  );
}
